import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { toast } from "react-toastify";
import BuildContent from "../../../common/components/builder/BuildContent.jsx";
import { randOf } from "../../../common/helpers/generators.js";
import * as API from "../../lib/api.js";
import { GenerateColor } from "../../lib/builder/generate.js";
import { AiGenerateECommerce, AiGeneratePage } from "../../lib/e-commerce.js";
import * as Links from "../../lib/links.js";
import pSleep from "../../lib/p-sleep.js";
import { ProductTypes } from "../../lib/product-types.js";
import Loader from "../Loader/Loader.jsx";
import { PreviewBar } from "../Preview";
import Wait from "../Wait/Wait.jsx";

export default function WebsiteDesign() {
    const { i18n, t } = useTranslation();
    const { id } = useParams();

    const history = useHistory();

    const [wait, setWait] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    const [website, setWebsite] = React.useState({
        project: null,
        page: null,
        variables: {},
        structure: [],
        svgBgPrev: null,
    });

    const [prevVariablesJSON, setPrevVariablesJSON] = React.useState(null);

    const { project, page, variables, structure, svgBgPrev } = website;

    const [showPicker, setShowPicker] = React.useState(false);

    React.useEffect(() => {
        id && Load(id);
    }, [id]);

    React.useEffect(() => {
        setWait(true);
        setTimeout(() => {
            setWait(false);
        }, 500);
    }, []);

    React.useEffect(() => {
        removeEventListener("keydown", OnShortcutSave);
        addEventListener("keydown", OnShortcutSave);

        return () => {
            removeEventListener("keydown", OnShortcutSave);
        };
    }, [website]);

    function OnShortcutSave(event) {
        if (!(event.key === "s" && (event.ctrlKey || event.metaKey))) {
            return;
        }

        event.preventDefault();
        Save(true, false, event.shiftKey);
    }

    function Load(id_project) {
        setLoading(true);
        return Promise.all([API.Send("/project/get", { id_project }), API.Send("/page/list", { id_project })])
            .then(async ([project, { pages }]) => {
                const page = pages[pages.length - 1];

                // !TODO Must be "common"
                const { pack } = await API.Send("/lang/pack", { type: "panel", lang: project.variables.Localisation });
                i18n.addResourceBundle(project.variables.Localisation, "translation", pack, true, true);

                setWebsite({
                    ...website,
                    project,
                    page,
                    variables: { ...project.variables, __domain: project.domain },
                    structure: page.structure,
                    svgBgPrev: project.variables.svgBg,
                });

                setPrevVariablesJSON(JSON.stringify(project.variables));
            })
            .finally(() => setLoading(false));
    }

    function onCancelClick() {
        history.push(Links.Dashboard());
    }

    function onEditColorClick() {
        setShowPicker(!showPicker);
    }

    async function onEditDesignClick() {
        if (wait) {
            return;
        }

        setWait(true);

        await pSleep(500);

        await API.CompileGenerate({ variables, structure }).then(
            ({ variables: newVariables, structure: newStructure }) => {
                const { ProductType, ProductPicture, FullWidthBg } = variables;

                const AdventurePictureExample = ProductPicture
                    ? undefined
                    : FullWidthBg && randOf(ProductTypes.filter(type => type.value === ProductType)[0].images);

                setWebsite({
                    ...website,
                    variables: {
                        ...variables,
                        blocks: newVariables.blocks,
                        info: newVariables.info,
                        svgBg: newVariables.svgBg,
                        font: newVariables.font,
                        color: { ...variables.color, ...newVariables.color },
                        eStyles: newVariables.eStyles,
                        variables2block: newVariables.variables2block,
                        AdventurePictureExample,
                    },
                    structure: newStructure,
                });
            }
        );

        await pSleep(500);

        setWait(false);
    }

    function onEditContentClick() {
        history.push(Links.WebsiteEditContent(id));
    }

    function onEditECommerceClick() {
        history.push(Links.WebsiteEditECommerce(id));
    }

    function onColorDefault() {
        const newVariables = { ...variables, color: { ...variables?.color } };
        // newVariables.svgBg = svgBgPrev;

        if (newVariables.color?.userPrimary) {
            delete newVariables.color.userPrimary;
        }

        if (newVariables.color?.userSecondary) {
            delete newVariables.color.userSecondary;
        }

        Promise.resolve()
            .then(() => {
                if (newVariables.svgBg) {
                    return API.SvgBgUpdate(
                        newVariables.svgBg,
                        newVariables.color.userPrimary ?? newVariables.color.primary,
                        newVariables.color.userSecondary ?? newVariables.color.secondary
                        // ).then(({ url, withGradient }) => {
                    ).then(response => {
                        if (!response.ok) {
                            return;
                        }

                        newVariables.svgBg = response.url;
                        newVariables.withGradient = response.withGradient || false;
                    });
                }
            })
            .then(() => {
                setWebsite({ ...website, variables: newVariables });
                setShowPicker(false);
            });
    }

    function onColorApply(newColor, close = true) {
        const isColorDifference = newColor.hex !== (variables.color.userPrimary ?? variables.color.primary);
        const userColor = isColorDifference ? { userPrimary: newColor.hex /* , userSecondary: newColor.hex */ } : {};
        // const color = { ...variables.color, ...userColor };

        let color = { ...variables.color, ...userColor };
        let svgBg = variables.svgBg;
        let withGradient = variables.withGradient;

        GenerateColor(color)
            .then(newColor => (color = newColor))
            .then(() => {
                if (variables.svgBg && isColorDifference) {
                    return API.SvgBgUpdate(
                        variables.svgBg,
                        color.userPrimary ?? color.primary,
                        color.userSecondary ?? color.secondary
                    ).then(res => {
                        svgBg = res.url;
                        withGradient = res.withGradient;
                    });
                }
            })
            .then(() => {
                setWebsite({
                    ...website,
                    variables: {
                        ...variables,
                        color,
                        svgBg,
                        eStyles: {
                            ...variables.eStyles,
                            withGradient,
                        },
                    },
                });

                close && setShowPicker(false);
            });
    }

    function onSavePublishClick(event) {
        setShowPicker(false);
        const ctrl = event.metaKey || event.ctrlKey;
        const shift = event.shiftKey;
        Save(true, !ctrl, ctrl && shift);
    }

    async function Save(publish = false, back = true, ai = false) {
        setSaving(true);

        const _variables = JSON.parse(JSON.stringify(variables));

        if (ai) {
            let response;
            if (variables.ProductType === "ecommerce") {
                response = await AiGenerateECommerce(project.id);
            } else {
                response = await AiGeneratePage(project.id);
            }

            if ((!response?.ok || response?.errors) && !response?.aiVariables && !response?.aiStructure) {
                console.warn(`[WARN/Save()]`, response);
                setSaving(false);
                return;
            }

            try {
                if (response?.aiVariables?.Color) {
                    delete response.aiVariables.Color;
                }

                for (const key in response?.aiVariables || {}) {
                    _variables[key] = response?.aiVariables?.[key];
                }
            } catch (e) {
                setSaving(false);
                return;
            }
        }

        if (prevVariablesJSON !== JSON.stringify(_variables)) {
            _variables.dateModified = Date.now();
        }

        return Promise.all([
            API.ProjectSet({ ...project, id_project: project.id, variables: _variables }),
            API.PageUpdate(page.id, structure),
            publish && API.CompileRender(project.domain),
        ])
            .then(() => svgBgPrev && svgBgPrev !== _variables.svgBg && API.SvgBgUnsign(svgBgPrev))
            .then(() => _variables.svgBg && API.SvgBgSign(_variables.svgBg))
            .then(() => {
                if (back) {
                    back && history.push(Links.Dashboard());
                } else {
                    project.id && Load(project.id);
                }
            })
            .catch(e => {
                toast.error(`[ERROR] ${e.message}`);
                console.warn(`[ERROR] Save(): ${e.message}`);
            })
            .finally(() => setSaving(false));
    }

    return (
        <>
            <div className="preview">
                <div className="preview-menu">
                    <div className="preview-menu-logo">{/* pgbldr.com */}</div>

                    <div className="preview-bar">
                        <PreviewBar
                            loading={loading}
                            onCancelClick={onCancelClick}
                            onEditColorClick={onEditColorClick}
                            onEditDesignClick={onEditDesignClick}
                            disableEditDesign={wait}
                            onEditContentClick={onEditContentClick}
                            onEditECommerceClick={variables?.ProductType === "ecommerce" && onEditECommerceClick}
                            onSavePublishClick={onSavePublishClick}
                            variables={variables}
                            showPicker={showPicker}
                            onColorDefault={onColorDefault}
                            onColorApply={onColorApply}
                        />
                    </div>
                </div>

                {loading && <h2 className="panel-heading-text">{t("Loading")}...</h2>}
                {!loading && <BuildContent {...{ edit: { id }, variables, structure }} />}
            </div>

            <Loader show={wait} />
            <Wait show={saving} />
        </>
    );
}
